// JoystickPan.js
import React, { useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { useThree, useFrame } from "@react-three/fiber";

/** Public hook: create one joystick instance you can share */
export function useJoystick(dead = 0.12, radiusPx = 64) {
  const [vec] = useState(() => new THREE.Vector2()); // normalized [-1..1]
  const [active, setActive] = useState(false);

  const api = useMemo(
    () => ({
      vec,
      get active() {
        return active;
      },
      dead,
      radiusPx,
      setFromPixels(dx, dy) {
        const r = Math.min(Math.hypot(dx, dy), radiusPx);
        const ang = Math.atan2(dy, dx);
        vec.set((r * Math.cos(ang)) / radiusPx, (r * Math.sin(ang)) / radiusPx);
      },
      reset() {
        vec.set(0, 0);
      },
    }),
    [vec, active, dead, radiusPx]
  );

  return { joystick: api, setActive };
}

/** Driver that lives INSIDE the Canvas (moves camera/controls each frame) */
export function JoystickPanDriver({
  joystick,
  controlsRef,
  enabled = true,
  basePanSpeed = 60.0,
}) {
  const { camera, clock } = useThree();
  const panRight = useMemo(() => new THREE.Vector3(), []);
  const panUp = useMemo(() => new THREE.Vector3(), []);
  const panOffset = useMemo(() => new THREE.Vector3(), []);
  const dir = useMemo(() => new THREE.Vector3(), []);

  useFrame(() => {
    const controls = controlsRef?.current;
    if (!enabled || !controls) return;

    const v = joystick.vec;
    const len = v.length();
    if (len < joystick.dead) return;

    // const dt = clock.getDelta();
    const dt = Math.min(clock.getDelta(), 1 / 60);  // cap delta (anti-jump)

    // camera basis
    camera.getWorldDirection(dir);
    panRight.crossVectors(dir, camera.up).normalize();
    panUp.copy(camera.up).normalize();

    const target = controls.target ?? new THREE.Vector3(0, 0, 0);

    // For orthographic cams, scaling by distance still works fine,
    // but you can replace distance with a constant if you prefer.
    const distance = camera.position.distanceTo(target);

    const strength = (len - joystick.dead) / (1 - joystick.dead);
    const speed = basePanSpeed * (0.25 + 0.75 * distance) * 1.5 * strength * dt;
    // const zoomScale = camera.isOrthographicCamera ? (1 / Math.max(0.001, camera.zoom)) : 1;
    // const speed = basePanSpeed * zoomScale * 1.25 * strength * dt;

    panOffset
      .copy(panRight)
      .multiplyScalar(v.x * speed)
      .addScaledVector(panUp, v.y * -1 *  speed);

    // --- Pre-clamp to your bounds (±100 * zoom) to avoid edge jitter ---
    // const limit = 100 * camera.zoom;
    // const nextTx = THREE.MathUtils.clamp(target.x + panOffset.x, -limit, limit);
    // const nextTy = THREE.MathUtils.clamp(target.y + panOffset.y, -limit, limit);
    // panOffset.x = nextTx - target.x;
    // panOffset.y = nextTy - target.y;
    // -------------------------------------------------------------------

    // Apply movement
    camera.position.add(panOffset);
    controls.target.add(panOffset);
    controls.update();
  });

  return null;
}

/** Visual overlay that lives OUTSIDE the Canvas */
export function JoystickOverlay({ joystickApi, setActive, className }) {
  const baseRef = useRef(null);
  const thumbRef = useRef(null);
  const center = useRef({ x: 0, y: 0 });
  const activeRef = useRef(false);
  const arrowRefs = {
     up: useRef(null),
     down: useRef(null),
     left: useRef(null),
     right: useRef(null),
  };

  const toThumbTransform = (kx, ky) =>
    `translate(-50%, -50%) translate(${kx}px, ${ky}px)`;

   const setActiveArrow = (vx, vy) => {
     // Clear all
     Object.values(arrowRefs).forEach(r => r.current && r.current.classList.remove('active'));
     // Deadzone: no highlight
     if (Math.hypot(vx, vy) < joystickApi.dead) return;
     // Decide dominant axis (4-way)
     if (Math.abs(vx) > Math.abs(vy)) {
       (vx > 0 ? arrowRefs.right : arrowRefs.left).current?.classList.add('active');
     } else {
       // Note: vy positive = up (our joystick vec uses +Y up)
       (vy > 0 ? arrowRefs.up : arrowRefs.down).current?.classList.add('active');
     }
   };

    const computeCenter = () => {
        if (!baseRef.current) return;
        const rect = baseRef.current.getBoundingClientRect();
        center.current = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
    };

  const startFromThumb = (e) => {
    if (!baseRef.current || !thumbRef.current) return;

    // Only start if the pointer is on the thumb
    if (e.target !== thumbRef.current) return;

    // const rect = baseRef.current.getBoundingClientRect();
    // center.current = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
    computeCenter();

    thumbRef.current.setPointerCapture(e.pointerId);
    if (thumbRef.current) thumbRef.current.style.transition = 'none';
    setActive(true);
    activeRef.current = true;
    handleMove(e);
  };

   // NEW: start drag from anywhere on the base; thumb jumps to that point
  const startFromBase = (e) => {
    if (!baseRef.current || !thumbRef.current) return;
    if (e.target === thumbRef.current) return; // thumb has its own handler
    computeCenter();
    // capture on the base so move events keep coming to us
    baseRef.current.setPointerCapture?.(e.pointerId);
    thumbRef.current.style.transition = 'none';
    setActive(true);
    activeRef.current = true;
    handleMove(e); // instantly move thumb to where user pressed
  };
   const endDrag = (e) => {
    if (thumbRef.current?.hasPointerCapture(e.pointerId)) {
      thumbRef.current.releasePointerCapture(e.pointerId);
    }
    baseRef.current?.releasePointerCapture?.(e.pointerId);
    joystickApi.reset();
    setActive(false);            // ✅ was true before (bug)
    activeRef.current = false;

    setActiveArrow(0, 0); // clear highlights
    // Smooth snap to center (CSS transition handles the animation)
    if (thumbRef.current) {
      thumbRef.current.style.transition = 'transform 120ms ease';
      thumbRef.current.style.transform = toThumbTransform(0, 0);
    }
  };

  const handleMove = (e) => {
    if (!activeRef.current) return;

    const dx = e.clientX - center.current.x;
    const dy = e.clientY - center.current.y;
    joystickApi.setFromPixels(dx, dy);

    const r = joystickApi.radiusPx;
    const len = Math.min(Math.hypot(dx, dy), r);
    const ang = Math.atan2(dy, dx);
    const kx = Math.cos(ang) * len;
    const ky = Math.sin(ang) * len;

    if (thumbRef.current) {
      thumbRef.current.style.transform = toThumbTransform(kx, ky);
    }
    // Highlight arrows based on normalized vec (vx right +, vy up +)
    setActiveArrow(joystickApi.vec.x, -joystickApi.vec.y); // flip Y for visual highlight
  };

  return (
    <div className={className ?? "pointer-events-none select-none"}>
      <div
        ref={baseRef}
        className="joystick-base pointer-events-auto"
        style={{ touchAction: "none" }}
        onPointerDown={startFromBase}   // 👈 enable base-click start
        // Base listens only for move/end; start is on the thumb
        onPointerMove={handleMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onContextMenu={(e) => e.preventDefault()}
      >
        {/* Directional arrows */}
        <div ref={arrowRefs.up} className="joystick-arrow joystick-arrow--up">
          <svg viewBox="0 0 24 24" className="joystick-arrow-svg">
            <path d="M12 4 L5 14 H19 Z" />
          </svg>
        </div>
        <div ref={arrowRefs.down} className="joystick-arrow joystick-arrow--down">
          <svg viewBox="0 0 24 24" className="joystick-arrow-svg">
            <path d="M12 20 L5 10 H19 Z" />
          </svg>
        </div>
        <div ref={arrowRefs.left} className="joystick-arrow joystick-arrow--left">
          <svg viewBox="0 0 24 24" className="joystick-arrow-svg">
            <path d="M4 12 L14 5 V19 Z" />
          </svg>
        </div>
        <div ref={arrowRefs.right} className="joystick-arrow joystick-arrow--right">
          <svg viewBox="0 0 24 24" className="joystick-arrow-svg">
            <path d="M20 12 L10 5 V19 Z" />
          </svg>
        </div>

        <div className="joystick-center" />
        <div
          ref={thumbRef}
          className="joystick-thumb"
          onPointerDown={startFromThumb}   // ✅ start only when grabbing the thumb
        />
      </div>
    </div>
  );
}
